import type { Next, NextStage } from '@zevik/schemas';

/**
 * Which screen an assessment page shows, read off `next` and nothing else
 * (docs/DECISIONS.md ZD-124 §2).
 *
 * ## The one rule, and what it is not
 *
 * `next` says what the assessment is waiting on; this says which of the
 * console's screens serves that. The three kinds with a stage map one to one.
 * `person` carries a list of events, and the mapping from an *event* to a
 * screen is the whole of what this module knows: it names no assessment
 * state and no order of states, because a page that decided *this state
 * needs a person* from a state name would be the transition table in a
 * bundle (ZD-121 §5.6). `client.test.ts` asserts that no shipped module in
 * this app names a state.
 *
 * ## Choosing among a person's events
 *
 * `artefact_uploaded` is on every person list, because a person may supply a
 * revision at every state but `closed` (ZD-069) — so it is never the act a
 * state is *for*, and it is offered as a secondary control wherever the list
 * names it. Of the other events the console serves, exactly one screen must
 * result: none means the state is waiting on an act this console has no
 * screen for, and that is **declared** with the events by name rather than
 * papered over with the nearest screen (ZD-035 §2); two means two screens
 * compete, which no state on today's table produces and which this refuses
 * to resolve by preference — a preference would be an ordering this module
 * has no business holding.
 *
 * The events at the gate are `approver_decided`, `rework_analysis_requested`
 * and `clarification_required`, and all three are the reviewer's, so they
 * map to one screen; `domains_confirmed` is the domain confirmation.
 *
 * ## Past the gate (docs/DECISIONS.md ZD-125 §4)
 *
 * `report_generated` is the act of generating the report, and `report` is
 * its screen. `actions_exported` is the act that follows a generated
 * report, and the console has no way to perform it — nothing in the API
 * produces it either — so its screen, `reported`, is for the reading the
 * state is actually good for: the report is there, and the screen says the
 * next act is not on a screen. `assessment_closed` and `monitoring_started`
 * stay declared, and are unreachable until something produces
 * `actions_exported`.
 */

export type Screen =
  | { kind: 'running'; stage: NextStage }
  | { kind: 'ready'; stage: NextStage; upload: boolean }
  | {
      kind: 'stopped';
      stage: NextStage;
      upload: boolean;
      run: Extract<Next, { kind: 'stopped' }>['run'];
    }
  /** Nothing to run and nothing to confirm: the documents are the whole screen. */
  | { kind: 'documents' }
  | { kind: 'domains'; upload: boolean }
  | { kind: 'review'; upload: boolean }
  /** Approved: the report is the next act, and it is a press. */
  | { kind: 'report'; upload: boolean }
  /** A report exists, and what follows it is not on a screen. */
  | { kind: 'reported'; upload: boolean }
  /** An act this console has no screen for — declared, with the events by name. */
  | { kind: 'unserved'; events: string[]; upload: boolean };

const UPLOAD_EVENT = 'artefact_uploaded';

/** The person's events this console has a screen for, and which screen. */
type ServedKind = 'domains' | 'review' | 'report' | 'reported';

const SERVED: ReadonlyMap<string, ServedKind> = new Map([
  ['domains_confirmed', 'domains'],
  ['approver_decided', 'review'],
  ['rework_analysis_requested', 'review'],
  ['clarification_required', 'review'],
  ['report_generated', 'report'],
  ['actions_exported', 'reported'],
]);

export function screenFor(next: Next): Screen {
  switch (next.kind) {
    case 'running':
      return { kind: 'running', stage: next.stage };
    case 'ready':
      return { kind: 'ready', stage: next.stage, upload: next.events.includes(UPLOAD_EVENT) };
    case 'stopped':
      return {
        kind: 'stopped',
        stage: next.stage,
        upload: next.events.includes(UPLOAD_EVENT),
        run: next.run,
      };
    case 'person': {
      const upload = next.events.includes(UPLOAD_EVENT);
      const screens = new Set<ServedKind>();
      for (const event of next.events) {
        const screen = SERVED.get(event);
        if (screen !== undefined) screens.add(screen);
      }
      const others = next.events.filter((event) => event !== UPLOAD_EVENT);
      if (screens.size === 1) return { kind: [...screens][0]!, upload };
      if (others.length === 0 && upload) return { kind: 'documents' };
      return { kind: 'unserved', events: others, upload };
    }
  }
}

/**
 * The masthead's word for where the assessment stands, read off the screen
 * and never off the state (ZD-124 §9). The first build rendered
 * `state.split('_').join(' ')` — `canonical model created`, the machine's
 * own vocabulary in front of a client, and invisible to the state-name guard
 * because the string arrived from the API. The chip is now one of these
 * nine words, which is the screen a person is looking at said shortly.
 */
export const CHIP_WORDS: Readonly<Record<Screen['kind'], string>> = {
  documents: 'awaiting documents',
  ready: 'ready to run',
  running: 'running',
  stopped: 'stopped',
  domains: 'confirm the scope',
  review: 'awaiting your review',
  report: 'approved',
  reported: 'report ready',
  unserved: 'waiting',
};

export function chipFor(screen: Screen): { text: string; open: boolean } {
  return { text: CHIP_WORDS[screen.kind], open: screen.kind === 'review' };
}
